import { useTranslate } from '@refinedev/core';
import { Card, Flex, Grid, Rate, Tabs, Typography, theme } from 'antd';
import { StatCell } from '@/components/common/show/StatCell';
import type { PublicationListOut, ReviewOut } from '@/api/generated/schemas';
import { ReviewStatusTag } from '@/components/feedback/tags';
import { PublicationInfoPopover } from '@/components/user_catalog/publications/PublicationInfoPopover';
import { REVIEW_SHOW_TABS } from '../constants';
import type { ReviewShowTab } from '../constants';

const { Text, Title } = Typography;

const TAB_LABEL_KEYS: Record<ReviewShowTab, string> = {
  general: 'base.sections.basic_info',
};

interface ReviewShowHeaderProps {
  record: ReviewOut;
  publication?: PublicationListOut;
  activeTab: ReviewShowTab;
  onTabChange: (key: string) => void;
}

export const ReviewShowHeader = ({
  record,
  publication,
  activeTab,
  onTabChange,
}: ReviewShowHeaderProps) => {
  const t = useTranslate();
  const { token } = theme.useToken();
  const screens = Grid.useBreakpoint();

  return (
    <Card styles={{ body: { paddingBottom: 0 } }}>
      <Flex gap={token.marginLG} align="flex-start" vertical={!screens.sm}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <Flex gap={token.marginSM} align="baseline" wrap>
            {/* Long names wrap instead of pushing the tabs bar out. */}
            <Title
              level={3}
              style={{
                margin: 0,
                fontSize: token.fontSizeHeading3,
                overflowWrap: 'anywhere',
              }}
            >
              {record.author_name}
            </Title>
            <ReviewStatusTag record={record} />
          </Flex>

          <Text
            type="secondary"
            style={{ display: 'block', marginTop: token.marginXXS }}
          >
            {`ID ${record.id}`}
          </Text>

          <Flex
            gap={token.marginXL + token.margin}
            style={{ marginTop: token.marginLG }}
            wrap
          >
            <StatCell label={t('feedback.reviews.fields.rating')}>
              <Rate allowHalf disabled value={record.rating} />
            </StatCell>
            <StatCell label={t('feedback.reviews.fields.publication_id')}>
              <PublicationInfoPopover
                publication={publication}
                publicationId={record.publication_id}
              />
            </StatCell>
          </Flex>
        </div>
      </Flex>

      <Tabs
        activeKey={activeTab}
        onChange={onTabChange}
        style={{ marginTop: token.marginSM }}
        tabBarStyle={{ marginBottom: 0 }}
        items={REVIEW_SHOW_TABS.map((key) => ({
          key,
          label: t(TAB_LABEL_KEYS[key]),
        }))}
      />
    </Card>
  );
};
